import { Button } from "@shared/components/ui/Button";
import { Organization } from "@shared/types/organization";
import theme from "@shared/utils/themeColors";
import React from "react";
import { Link } from "react-router-dom";
import { getOrganizationLogo } from "../utils/organization.utils";

// Helper function to get organization profile path
const getOrganizationProfilePath = (id: string) =>
  `/community/organizations/${id}`;

interface OrganizationCardProps {
  organization: Organization;
}

export const OrganizationCard: React.FC<OrganizationCardProps> = ({
  organization,
}) => {
  const { id, name } = organization;

  return (
    <div
      className={`col-span-full sm:col-span-6 xl:col-span-3 ${theme.bg.primary} shadow-sm rounded-xl`}
    >
      <div className="flex flex-col h-full">
        {/* Card top */}
        <div className="p-5">
          {/* Image + name */}
          <header>
            <div className="flex justify-center mb-2">
              <Link
                className="relative inline-flex items-start"
                to={getOrganizationProfilePath(id)}
              >
                <div
                  className="absolute top-0 right-0 -mr-2 bg-white dark:bg-gray-700 rounded-full shadow"
                  aria-hidden="true"
                >
                  <svg
                    className="w-8 h-8 fill-current text-blue-500"
                    viewBox="0 0 32 32"
                  >
                    <path d="M21 14.077a.75.75 0 01-.75-.75 1.5 1.5 0 00-1.5-1.5.75.75 0 110-1.5 1.5 1.5 0 001.5-1.5.75.75 0 111.5 0 1.5 1.5 0 001.5 1.5.75.75 0 010 1.5 1.5 1.5 0 00-1.5 1.5.75.75 0 01-.75.75zM14 24.077a1 1 0 01-1-1 4 4 0 00-4-4 1 1 0 110-2 4 4 0 004-4 1 1 0 012 0 4 4 0 004 4 1 1 0 010 2 4 4 0 00-4 4 1 1 0 01-1 1z" />
                  </svg>
                </div>
                <img
                  className="rounded-sm h-[120px] object-contain"
                  src={getOrganizationLogo(organization)}
                  alt={name}
                />
              </Link>
            </div>
            <div className="text-center">
              <Link
                className={`inline-flex ${theme.text.primary} hover:text-gray-900 dark:hover:text-white`}
                to={getOrganizationProfilePath(id)}
              >
                <h2 className="text-xl leading-snug justify-center font-semibold">
                  {name}
                </h2>
              </Link>
            </div>
            {/* Location */}
            {organization.location && (
              <div className="flex justify-center items-center">
                <span
                  className={`text-sm font-medium text-gray-400 dark: ${theme.text.muted}`}
                >
                  <svg
                    className={`fill-current shrink-0 text-gray-400 dark: ${theme.text.muted}`}
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 8.992a2 2 0 1 1-.002-3.998A2 2 0 0 1 8 8.992Zm-.7 6.694c-.1-.1-4.2-3.696-4.2-3.796C1.7 10.69 1 8.892 1 6.994 1 3.097 4.1 0 8 0s7 3.097 7 6.994c0 1.898-.7 3.697-2.1 4.996-.1.1-4.1 3.696-4.2 3.796-.4.3-1 .3-1.4-.1Zm-2.7-4.995L8 13.688l3.4-2.997c1-1 1.6-2.198 1.6-3.597 0-2.798-2.2-4.996-5-4.996S3 4.196 3 6.994c0 1.399.6 2.698 1.6 3.697 0-.1 0-.1 0 0Z" />
                  </svg>
                </span>{" "}
                <span className="truncate">{organization.location}</span>
              </div>
            )}
          </header>
          {/* Description */}
          <div className="text-center mt-2">
            <div className="text-sm">{organization.industry}</div>
          </div>
        </div>
        {/* Card footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-3">
          <div className="flex justify-center">
            <Button
              as="navlink"
              to={getOrganizationProfilePath(id)}
              variant="link"
            >
              View
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
